import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './client.js';

import type {
  AlertAction,
  AlertActionResponse,
  AlertContextResponse,
  AlertDetail,
  AlertsResponse,
  AlertView,
  AssembliesResponse,
  ConnectorsResponse,
  Dashboard,
  DraftResponse,
  FreshnessResponse,
  ManualCompareRequest,
  ManualCompareResponse,
  RevisionsResponse,
  SearchResponse,
  Severity,
  SourceSystem,
} from '@baseline/shared';

// Server state through TanStack Query (CLAUDE.md section 7). Live updates invalidate these
// keys; while the stream is down every query polls every 15 s (`live/useLiveUpdates.ts`).

/** The Alerts list filter. It lives in the URL, so the detail stepper and links keep it. */
export interface AlertFilter {
  view: AlertView;
  severity: Severity | null;
  assemblyId: string | null;
  state: string | null;
}

export function filterQuery(f: AlertFilter): string {
  const p = new URLSearchParams();
  if (f.view !== 'all') p.set('view', f.view);
  if (f.severity !== null) p.set('severity', String(f.severity));
  if (f.assemblyId) p.set('assemblyId', f.assemblyId);
  if (f.state) p.set('state', f.state);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const keys = {
  dashboard: ['dashboard'] as const,
  alerts: (f: AlertFilter) => ['alerts', 'list', f] as const,
  alert: (id: string, f: AlertFilter) => ['alerts', 'detail', id, f] as const,
  context: (id: string) => ['alerts', 'context', id] as const,
  draft: (id: string) => ['drafts', id] as const,
  connectors: ['connectors'] as const,
  freshness: ['freshness'] as const,
  assemblies: ['assemblies'] as const,
  revisions: (assemblyId: string, system: SourceSystem) =>
    ['revisions', assemblyId, system] as const,
  search: (q: string) => ['search', q] as const,
};

export function useDashboard() {
  return useQuery({ queryKey: keys.dashboard, queryFn: () => api<Dashboard>('/dashboard') });
}

export function useAlerts(f: AlertFilter) {
  return useQuery({
    queryKey: keys.alerts(f),
    queryFn: () => api<AlertsResponse>(`/alerts${filterQuery(f)}`),
  });
}

export function useAlert(id: string, f: AlertFilter) {
  return useQuery({
    queryKey: keys.alert(id, f),
    queryFn: () => api<AlertDetail>(`/alerts/${id}${filterQuery(f)}`),
  });
}

export function useAlertContext(id: string) {
  return useQuery({
    queryKey: keys.context(id),
    queryFn: () => api<AlertContextResponse>(`/alerts/${id}/context`),
  });
}

export function useDraft(id: string | null) {
  return useQuery({
    queryKey: keys.draft(id ?? 'none'),
    queryFn: () => api<DraftResponse>(`/drafts/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useConnectors() {
  return useQuery({
    queryKey: keys.connectors,
    queryFn: () => api<ConnectorsResponse>('/connectors'),
  });
}

export function useFreshness() {
  return useQuery({
    queryKey: keys.freshness,
    queryFn: () => api<FreshnessResponse>('/sources/freshness'),
  });
}

export function useAssemblies() {
  return useQuery({
    queryKey: keys.assemblies,
    queryFn: () => api<AssembliesResponse>('/assemblies'),
    staleTime: 60_000,
  });
}

export function useRevisions(assemblyId: string | null, system: SourceSystem | null) {
  return useQuery({
    queryKey: keys.revisions(assemblyId ?? '', system ?? 'PDM'),
    queryFn: () =>
      api<RevisionsResponse>(`/assemblies/${assemblyId ?? ''}/revisions?system=${system ?? ''}`),
    enabled: assemblyId !== null && system !== null,
  });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => api<SearchResponse>(`/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length > 0,
    placeholderData: (prev) => prev,
  });
}

export function useManualCompare(req: ManualCompareRequest | null) {
  return useQuery({
    queryKey: ['compare', req],
    queryFn: () => api<ManualCompareResponse>('/compare/manual', { method: 'POST', body: req }),
    enabled: req !== null,
    placeholderData: (prev) => prev,
    refetchInterval: false,
  });
}

/** Everything an alert change can move: lists, details, counts, the dashboard. */
export function useInvalidateAlerts() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['alerts'] }),
      qc.invalidateQueries({ queryKey: ['drafts'] }),
      qc.invalidateQueries({ queryKey: keys.dashboard }),
    ]);
}

/** `POST /alerts/:id/actions/:action`, always with expectedVersion (BR-032). */
export function useAlertAction(alert: Pick<AlertDetail, 'id' | 'version'>) {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({ action, body }: { action: AlertAction; body?: Record<string, unknown> }) =>
      api<AlertActionResponse>(`/alerts/${alert.id}/actions/${action}`, {
        method: 'POST',
        body: { expectedVersion: alert.version, ...body },
      }),
    onSettled: invalidate,
  });
}
